import { FC, MouseEvent } from 'react';
import { Box, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { PublicServiceMeta, PublicServiceModel } from '@/lib/models/public-service/public-service.model';
import Icon from '../icon';
import Text from '../text';
import Chip from '../chip';
import AccordionButton from '../accordion-button';
import RiskChip from '../chip/risk-chip';
import ServiceLink from '../service-link';

interface Props {
  publicService: PublicServiceModel | PublicServiceMeta;
  onInfoClick?: (event: MouseEvent, publicService: PublicServiceModel | PublicServiceMeta) => void;
  noDetails?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  description: {
    overflowY: 'auto',
  },
  noDetails: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
}));

const PublicServiceButton: FC<Props> = ({ publicService, onInfoClick, noDetails = false }) => {
  const styles = useStyles();
  const { description, supportLevels, riskLevel } = publicService;
  const firstSentence = description ? description.split('.').shift() : '';

  const RiskChipEl = <RiskChip value={riskLevel} size="small" />;

  const hasInfoButton = !!onInfoClick;

  const TitleEl = (
    <Stack direction="row" gap={1} alignItems="center" width="100%">
      <Stack flex={1} minWidth={0}>
        <ServiceLink service={publicService} size="small" action="none" />
      </Stack>
      {hasInfoButton && (
        <Stack flexShrink={0}>
          <Icon
            name="info-circle"
            onClick={(event: MouseEvent) => onInfoClick(event, publicService)}
            size="small"
            color="grey"
          />
        </Stack>
      )}
    </Stack>
  );

  if (noDetails) {
    return (
      <Stack
        alignItems="center"
        p={1}
        flex="1 1 auto"
        minHeight={40}
        className={styles.noDetails}
      >
        {TitleEl}
      </Stack>
    );
  }

  return (
    <AccordionButton title={TitleEl}>
      <Stack gap={1}>
        <Box maxHeight={100} className={styles.description}>
          <Text size="small">{firstSentence}</Text>
        </Box>
        <Stack direction="row">
          <Stack gap={1} width="40%">
            <Text bold size="small">
              Risk Level
            </Text>
            <Box>{RiskChipEl}</Box>
          </Stack>

          <Stack gap={1}>
            <Text bold size="small">
              Support Level
            </Text>
            <Stack gap={0.5} direction="row">
              {supportLevels.map((supportLevel) => (
                <Chip key={supportLevel} label={supportLevel} size="x-small" />
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </AccordionButton>
  );
};

export default PublicServiceButton;
